import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * A custom element that displays a sale label above the price.
 *
 * Horizon only morphs the variant picker itself on a variant change; every other
 * block listens for the variant update event and updates itself from the fetched
 * section HTML. This does the same: it copies its own counterpart's contents and
 * hidden state out of the new HTML, so the label appears on discounted variants
 * and disappears on full-price ones without a page reload.
 *
 * @extends {Component<{}>}
 */
class SaleLabel extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateLabel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateLabel);
  }

  /**
   * Updates the label's contents and visibility.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateLabel = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newLabel = event.detail.data.html.querySelector(`sale-label[data-block-id="${this.dataset.blockId}"]`);

    if (!newLabel) {
      // The block no longer renders for this variant (e.g. the label copy was cleared).
      this.hidden = true;
      this.replaceChildren();
      return;
    }

    const imported = /** @type {Element} */ (document.importNode(newLabel, true));
    this.replaceChildren(...imported.childNodes);
    this.hidden = newLabel.hasAttribute('hidden');
  };
}

if (!customElements.get('sale-label')) {
  customElements.define('sale-label', SaleLabel);
}
